import React, { useMemo } from 'react'

import {
  ModalControlsStyled,
  Button,
  StyledText,
  CopyTextButton,
} from '@clickcease/cc-client-components-lib'
import { Trans, useTranslation } from 'react-i18next'

import {
  BzInstructionsLink,
  BzSetupPopupContent,
  BzSetupPopupInstallationKeys,
  BzSetupPopupInstallationKeysTitle,
  BzSetupPopupStep,
  BzSetupPopupSteps,
  BzSetupPopupStyled,
  KeysTitleRow,
} from 'Features/bzSetupPopup/BzSetupPopup.styled'
import { InstallationKeyRow } from 'Features/bzSetupPopup/InstallationKeyRow/InstallationKeyRow'
import { useGetBzDetails } from 'Features/bzSetupPopup/api/getBzSecret'
import { BzDomain } from 'Types/TagsResponse'
import { getPluginUrl } from 'Consts/botMitigation'
import { replaceWWW } from 'Utils/stringHelper'
import { useDomainsWithSearch } from 'Hooks/useDomainsWithSearch'
import { useSplit } from 'Hooks/useSplit'

interface BzSetupPopupProps {
  onOpenChange: (a: boolean) => void
  domainAddress: string
  bzState?: number | null
  isContentOnly?: boolean
}

export const BzSetupInner = ({
  onOpenChange,
  domainAddress,
  bzState,
  isContentOnly,
}: BzSetupPopupProps) => {
  const isCopyAllEnabled = useSplit('CE_COPY_ALL_KEYS_BM')
  const { t } = useTranslation('domainManagement')
  const [bzDetails, tags] = useGetBzDetails()
  const bzDetailsData = useMemo(() => {
    if (!(bzDetails.data instanceof Error)) {
      return bzDetails.data
    }
  }, [bzDetails])
  const { domains } = useDomainsWithSearch()

  const authenticationKeys: Record<'domainKey' | 'authKey', string> = useMemo(() => {
    if (tags.data && !(tags.data instanceof Error) && tags.data.length > 0) {
      const domainAddressToFind = replaceWWW(domainAddress)
      const domainFound: BzDomain | undefined = tags.data.find((domain: BzDomain) => {
        const domainAddressClean = domain.value
          .substring(domain.value.indexOf('-') + 1, domain.value.length)
          ?.toLowerCase()
        return domain.active && replaceWWW(domainAddressClean) === domainAddressToFind
      })
      return { authKey: domainFound?.apiKey ?? '', domainKey: domainFound?.hash ?? '' }
    }
    return { authKey: '', domainKey: '' }
  }, [tags, domainAddress])

  const pluginUrl = getPluginUrl(bzState, domainAddress)

  const filteredDomainProperties = domains.find((domain) => domain.address === domainAddress)

  const keys = {
    domainKey: authenticationKeys.domainKey || '',
    secretKey: bzDetailsData?.secretKey || '',
    authKey: authenticationKeys.authKey || '',
  }

  const allKeys: string = JSON.stringify(keys)

  const hideKey = !filteredDomainProperties?.botZapping || !filteredDomainProperties?.wordpress // hide keys in case BM is diabled OR not a WP website

  return (
    <BzSetupPopupStyled>
      <BzSetupPopupContent>
        <BzSetupPopupSteps>
          <BzSetupPopupStep>
            <StyledText variant={'baseLight'}>{t('bzSetupPopup.step_1')}</StyledText>
          </BzSetupPopupStep>
          <BzSetupPopupStep>
            <StyledText variant={'baseLight'}>
              <Trans ns="domainManagement" i18nKey="bzSetupPopup.step_2">
                Download the
                <BzInstructionsLink target={'_blank'} href={pluginUrl}>
                  CHEQ Essentials Bot Mitigation plugin
                </BzInstructionsLink>
              </Trans>
            </StyledText>
          </BzSetupPopupStep>
          <BzSetupPopupStep>
            <StyledText variant={'baseLight'}>{t('bzSetupPopup.step_3')}</StyledText>
          </BzSetupPopupStep>
        </BzSetupPopupSteps>

        <BzSetupPopupInstallationKeys>
          <KeysTitleRow>
            <BzSetupPopupInstallationKeysTitle>
              {t('bzSetupPopup.installation_keys', { domain: domainAddress })}
            </BzSetupPopupInstallationKeysTitle>
            {isCopyAllEnabled && !hideKey && (
              <CopyTextButton buttonText={t('bzSetupPopup.copy_all_keys')} value={allKeys} />
            )}
          </KeysTitleRow>

          <InstallationKeyRow
            text={t('bzSetupPopup.domain_key')}
            keyCode={authenticationKeys.domainKey || ''}
          />
          {!hideKey && (
            <InstallationKeyRow
              text={t('bzSetupPopup.secret_key')}
              keyCode={bzDetailsData ? bzDetailsData.secretKey : ''}
            />
          )}
          {!hideKey && (
            <InstallationKeyRow
              text={t('bzSetupPopup.authentication_key')}
              keyCode={authenticationKeys.authKey || ''}
            />
          )}
        </BzSetupPopupInstallationKeys>
      </BzSetupPopupContent>
      {!isContentOnly && (
        <ModalControlsStyled>
          <Button
            text={t('tracking_params_modal.done')}
            onClick={() => {
              onOpenChange(false)
            }}
          />
        </ModalControlsStyled>
      )}
    </BzSetupPopupStyled>
  )
}
